import React, { useState, useEffect } from 'react';
import { schoolAdminAPI } from '../../services/api';
import toast from 'react-hot-toast';

const CREDIT_PRICE = 200; // should come from env
const MIN_MANUAL_AMOUNT = 10000;

const WalletPage = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [credits, setCredits] = useState(1);
  const [loading, setLoading] = useState(false);
  const [buyLoading, setBuyLoading] = useState(false);
  const [manualAmount, setManualAmount] = useState(MIN_MANUAL_AMOUNT);
  const [manualLoading, setManualLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);

  useEffect(() => {
    fetchWallet();
    fetchTransactions();
  }, []);

  const fetchWallet = async () => {
    try {
      const res = await schoolAdminAPI.getWallet();
      setWallet(res.data);
    } catch (error) {
      toast.error('Failed to load wallet');
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await schoolAdminAPI.getTransactions();
      setTransactions(res.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    }
  };

  const handleBuyCredits = async () => {
    setBuyLoading(true);
    try {
      const res = await schoolAdminAPI.initiatePayment({
        payment_type: 'credit_purchase',
        credits,
        callback_url: window.location.origin + '/admin/payment-callback',
      });
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast.error('Failed to initiate payment');
      setBuyLoading(false);
    }
  };

  const handleManualRequest = async () => {
    if (manualAmount < MIN_MANUAL_AMOUNT) {
      toast.error(`Minimum amount is ₦${MIN_MANUAL_AMOUNT}`);
      return;
    }
    setManualLoading(true);
    try {
      const res = await schoolAdminAPI.requestManualTopup({ amount: manualAmount });
      setBankDetails(res.data);
      toast.success('Manual top-up request created. Use bank details below.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Wallet & Credits</h1>

      {/* Balance */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600">Current Balance</p>
            <p className="text-4xl font-bold text-primary-600">{wallet?.credit_balance || 0} credits</p>
          </div>
        </div>
      </div>

      {/* Online Purchase */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Buy Credits Online (Paystack)</h2>
        <div className="flex items-end space-x-4">
          <div>
            <label className="block text-gray-700 mb-2">Number of Credits</label>
            <input
              type="number"
              min="1"
              value={credits}
              onChange={(e) => setCredits(parseInt(e.target.value) || 1)}
              className="border rounded-lg px-3 py-2 w-32"
            />
          </div>
          <div>
            <p className="text-gray-700 mb-2">Total: ₦{credits * CREDIT_PRICE}</p>
          </div>
          <button
            onClick={handleBuyCredits}
            disabled={buyLoading}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {buyLoading ? 'Processing...' : 'Buy Now'}
          </button>
        </div>
      </div>

      {/* Manual Top-up */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-xl font-semibold mb-4">Manual Bank Transfer</h2>
        <p className="text-gray-600 mb-4">Minimum amount: ₦{MIN_MANUAL_AMOUNT}</p>
        <div className="flex items-end space-x-4">
          <div>
            <label className="block text-gray-700 mb-2">Amount (₦)</label>
            <input
              type="number"
              min={MIN_MANUAL_AMOUNT}
              value={manualAmount}
              onChange={(e) => setManualAmount(parseInt(e.target.value) || MIN_MANUAL_AMOUNT)}
              className="border rounded-lg px-3 py-2 w-40"
            />
          </div>
          <button
            onClick={handleManualRequest}
            disabled={manualLoading}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {manualLoading ? 'Requesting...' : 'Request Bank Details'}
          </button>
        </div>

        {bankDetails && (
          <div className="mt-6 p-4 border border-green-200 bg-green-50 rounded-lg">
            <h3 className="font-semibold text-green-800 mb-2">Bank Transfer Details</h3>
            <p><strong>Bank:</strong> {bankDetails.bank_name}</p>
            <p><strong>Account Number:</strong> {bankDetails.account_number}</p>
            <p><strong>Account Name:</strong> {bankDetails.account_name}</p>
            <p><strong>Amount:</strong> ₦{bankDetails.amount}</p>
            <p><strong>Reference:</strong> <code className="bg-gray-100 px-2 py-1 rounded">{bankDetails.reference}</code></p>
            <p className="text-sm text-gray-600 mt-2">Please use this reference when making the transfer. Your wallet will be credited after admin verification.</p>
          </div>
        )}
      </div>

      {/* Transaction History */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Recent Transactions</h2>
        {transactions.length === 0 ? (
          <p>No transactions yet.</p>
        ) : (
          <table className="min-w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2">Date</th>
                <th className="text-left py-2">Description</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Amount</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id} className="border-b">
                  <td className="py-2">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="py-2">{t.description}</td>
                  <td className="py-2 capitalize">{t.transaction_type}</td>
                  <td className="py-2">{t.amount} credits</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default WalletPage;